import { Injectable, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { QuoteTimeSeriesType } from '../domain';
import { Breakpoint, BreakpointState, MediaQueryService } from '../../../../services/media-query.service';

/**
 * Tick Positions Service
 *
 * Service calculate tick positions for chart series data
 *
 */
@Injectable({
  providedIn: 'root'
})
export class TickPositionsService {
  private readonly mediaQueryService = inject(MediaQueryService);

  private static readonly ONE_DAY_MILLISECONDS: number = 24 * 60 * 60 * 1000;
  private static readonly MONTHS_THRESHOLD: number = 5;
  private static readonly YEARS_THRESHOLD: number = 10;
  private static readonly TOTAL_TICKS_THRESHOLD: number = 6;

  breakpointChangesSubscription: Subscription;
  isSmallScreen: boolean;

  constructor() {
    this.breakpointChangesSubscription = this.mediaQueryService.observe(Breakpoint.LT_LG)
      .subscribe((state: BreakpointState): void => {
        this.isSmallScreen = state.matches;
      });
  }

  /**
   * Calculate daily ticks positions
   *
   * @returns Ticks positions
   * @param seriesData
   */
  calculateDailyTicks(seriesData: QuoteTimeSeriesType): Array<number> {
    const positions: Array<number> = [];
    let tick: number = seriesData[0][0] as number;
    let year: number = new Date(tick).getFullYear();
    let month: number = new Date(tick).getMonth();
    let day: number = new Date(tick).getDate();

    positions.push(tick);
    for (const data of seriesData) {
      tick = data[0] as number;
      if (new Date(tick).getFullYear() === year) {
        if (new Date(tick).getMonth() === month) {
          if (new Date(tick).getDate() !== day) {
            day = new Date(tick).getDate();
            positions.push(tick);
          }
        } else {
          month = new Date(tick).getMonth();
          day = new Date(tick).getDate();
          positions.push(tick);
        }
      } else {
        year = new Date(tick).getFullYear();
        month = new Date(tick).getMonth();
        day = new Date(tick).getDate();
        positions.push(tick);
      }
    }
    return positions;
  }

  /**
   * Calculate two days ticks positions
   *
   * @param seriesData data
   * @returns Ticks positions
   */
  calculateTwoDaysTicks(seriesData: QuoteTimeSeriesType): Array<number> {
    const positions: Array<number> = [];
    const startTime: number = seriesData[0][0] as number;
    const endTime: number = seriesData[seriesData.length - 1][0] as number;
    const marketOpenTime = new Date(startTime).setHours(9, 30, 0);
    const nextMarketOpenTime = new Date(endTime).setHours(9, 30, 0);

    positions.push(marketOpenTime);
    positions.push(new Date(marketOpenTime).setHours(12, 0, 0));
    positions.push(new Date(marketOpenTime).setHours(14, 0, 0));

    positions.push(nextMarketOpenTime);
    positions.push(new Date(nextMarketOpenTime).setHours(12, 0, 0));
    positions.push(new Date(nextMarketOpenTime).setHours(14, 0, 0));

    return positions;
  }

  /**
   * Calculate 52 weeks ticks positions
   *
   * @param seriesData data
   * @returns Ticks positions
   */
  calculate52WeeksTicks(seriesData: QuoteTimeSeriesType): Array<number> {
    let newTick: number;
    let endMonth:  number;
    const positions: Array<number> = [];

    const endTime: number = seriesData[seriesData.length - 1][0] as number;
    let tick: number = seriesData[0][0] as number;
    let startMonth: number = tick;
    let month = new Date(tick).getMonth();
    do {
      if (new Date(tick).getMonth() !== month) {
        endMonth = tick - TickPositionsService.ONE_DAY_MILLISECONDS;
        newTick = Math.floor((startMonth + endMonth) / 2);
        positions.push(newTick);
        month = new Date(tick).getMonth();
        startMonth = tick;
      }
      tick += TickPositionsService.ONE_DAY_MILLISECONDS;
    } while (tick <= endTime);
    positions.push((startMonth + tick) / 2);

    this.recalStartTicker(positions, seriesData);
    this.recalEndTicker(positions, seriesData);

    return this.getSmallScreenPositions(positions, seriesData);
  }

  /**
   * Calculate three years ticks positions
   *
   * @returns Ticks positions
   * @param seriesData
   * @param shortenticks
   */
  calculateThreeYearsTicks(seriesData: QuoteTimeSeriesType, shortenticks: boolean = false): Array<number> {
    let tickMonth:  number;
    let positions: Array<number> = [];

    let tick: number = seriesData[seriesData.length - 1][0] as number;
    let month: number = new Date(tick).getMonth();

    positions.push(tick);
    for (let i = seriesData.length - 2; i >= 0; i--) {
      tick = seriesData[i][0] as number;
      tickMonth = new Date(tick).getMonth();
      const diff = (month - tickMonth + 12) % 12; // months backward from current to tickMonth
      if (diff === 3) {
        positions.unshift(tick);
        month = tickMonth;
      }
    }

    if (shortenticks && positions.length > TickPositionsService.TOTAL_TICKS_THRESHOLD ) {
      positions = this.shortenTickers(positions);
    }
    return this.getSmallScreenPositions(positions, seriesData);
  }

  /**
   * Calculate five years ticks positions
   *
   * @returns Ticks positions
   * @param seriesData
   */
  calculateFiveYearsTicks(seriesData: QuoteTimeSeriesType): Array<number> {
    let tickMonth:  number;
    const positions: Array<number> = [];

    let tick: number = seriesData[seriesData.length - 1][0] as number;
    let month: number = new Date(tick).getMonth();

    positions.push(tick);
    for (let i = seriesData.length - 2; i >= 0; i--) {
      tick = seriesData[i][0] as number;
      tickMonth = new Date(tick).getMonth();
      if (tickMonth !== month && (Math.abs(new Date(tick).getMonth() - month) === 6)) {
        positions.unshift(tick);
        month = tickMonth;
      }
    }
    return this.getSmallScreenPositions(positions, seriesData);
  }

  /**
   * Calculate ten years ticks positions
   *
   * @returns Ticks positions
   * @param seriesData
   * @param shortenticks
   */
  calculateTenYearsTicks(seriesData: QuoteTimeSeriesType, shortenticks: boolean = false): Array<number> {
    let tick: number;
    let endYear: number;
    let positions: Array<number> = [];

    let startYear: number = seriesData[0][0] as number;
    let year: number = new Date(startYear).getFullYear();

    for (let i = 0; i < seriesData.length; i++) {
      tick = seriesData[i][0] as number;
      if (new Date(tick).getFullYear() !== year) {
        endYear = seriesData[i - 1][0] as number;
        positions.push(Math.floor((startYear + endYear) / 2));
        year = new Date(tick).getFullYear();
        startYear = tick;
      }
    }
    positions.push((startYear + tick) / 2);

    // reduce ticks if there is no space to show the first tick.
    if (positions.length > TickPositionsService.YEARS_THRESHOLD && new Date(positions[0]).getMonth() > 9 ) {
      positions.splice(0, 1);
    }
    if (shortenticks && positions.length > TickPositionsService.TOTAL_TICKS_THRESHOLD) {
      positions = this.shortenTickers(positions);
    }
    return this.getSmallScreenPositions(positions, seriesData);
  }

  /**
   * Calculate twenty years ticks positions
   *
   * @returns Ticks positions
   * @param seriesData
   * @param shortenticks
   */
  calculateTwentyYearsTicks(seriesData: QuoteTimeSeriesType, shortenticks: boolean = false): Array<number> {
    let isIncluded = false;
    let positions: Array<number> = [];
    let tick: number = seriesData[0][0] as number;
    let year: number = new Date(tick).getFullYear();
    const current: number = new Date().getFullYear();

    positions.push(tick);
    for (const data of seriesData) {
      tick = data[0] as number;
      const next = new Date(tick).getFullYear();
      if ((Math.abs(year - next) === 2)) {
        positions.push(tick);
        year = next;
        if (next === current) {
          isIncluded = true;
        }
      }
      // always show this year's tick
      if (!isIncluded && next === current) {
        positions.push(tick);
        isIncluded = true;
      }
    }

    if (shortenticks && positions.length > TickPositionsService.TOTAL_TICKS_THRESHOLD) {
      positions = this.shortenTickers(positions);
    }
    return this.getSmallScreenPositions(positions, seriesData);
  }

  /**
   * Remove/Recalculate start ticker based on start date
   *
   * @param positions
   * @param seriesData
   */
  private recalStartTicker(positions: Array<number>, seriesData: QuoteTimeSeriesType): void {
    if (positions.length > TickPositionsService.MONTHS_THRESHOLD && new Date(positions[0]).getDate() > 24) {
      positions.splice(0, 1);
    }
    if (new Date(positions[0]).getDate() > 19) {
      positions[0] = Math.floor((positions[0] + (seriesData[0][0] as number)) / 2);
    }
  }

  /**
   * Remove/Recalculate end ticker based on the end date
   *
   * @param positions
   * @param seriesData
   */
  private recalEndTicker(positions: Array<number>, seriesData: QuoteTimeSeriesType): void {
    if (positions.length > TickPositionsService.MONTHS_THRESHOLD && new Date(positions[positions.length - 1]).getDate() < 6) {
      positions.splice(positions.length - 1, 1);
    }
    if (new Date(positions[positions.length - 1]).getDate() < 10) {
      positions[positions.length - 1] = Math.floor((positions[positions.length - 1] + (seriesData[seriesData.length - 1][0] as number)) / 2);
    }
  }

  /**
   * Shorten Tickers
   *
   * @returns positions
   * @param positions
   */
  private shortenTickers(positions: Array<number>): Array<number> {
    let filtered: Array<number> = [];
    if (positions.length > 0) {
      filtered = positions.filter((_: unknown, index: number): boolean => (index % 2 === 0));
    }
    return filtered;
  }

  private getSmallScreenPositions(positions: Array<number>, seriesData?: QuoteTimeSeriesType): Array<number> {
    if (this.isSmallScreen && positions.length >= 6) {
      const filteredPositions: Array<number> = [];
      filteredPositions.push(seriesData[0][0] as number);
      filteredPositions.push(seriesData[Math.round((seriesData.length - 1) / 4)][0] as number);
      filteredPositions.push(seriesData[Math.round((seriesData.length - 1) / 2)][0] as number);
      filteredPositions.push(seriesData[Math.round(((seriesData.length - 1) * 3) / 4)][0] as number);
      filteredPositions.push(seriesData[seriesData.length - 1][0] as number);
      return filteredPositions;
    }
    return positions;
  }
}
